/**
 * AI 对话服务
 * 处理多轮对话的 AI 交互
 */

import { DialogTurn } from '../../db/shadowDb';
import { callGeminiAudio, callGeminiText } from '../../session/speaking/genaiClient';

export interface DialogResponse {
  /** AI 回复内容 */
  aiMessage: string;
  /** 用户表达的评分 */
  score?: number;
  /** 表达改进建议 */
  suggestions?: string[];
  /** 升级表达词汇 */
  vocabUpgrades?: Array<{
    original: string;
    upgrade: string;
    reason: string;
  }>;
}

/**
 * 构建对话 Prompt
 */
function buildDialogPrompt(
  topic: string,
  targetBand: number,
  turns: DialogTurn[],
  userTranscript: string
): string {
  // 构建对话历史
  const historyLines = turns.map((turn) => {
    const role = turn.role === 'ai' ? 'AI' : 'User';
    return `${role}: ${turn.content}`;
  }).join('\n');

  return `You are an IELTS speaking examiner conducting a Part 3 style discussion.

## Context
- Topic: ${topic}
- Target band: ${targetBand}
- This is turn ${turns.length + 1} of the conversation

## Conversation History
${historyLines || '(This is the start of the conversation)'}

## User's Latest Response
"${userTranscript}"

## Your Task
1. Evaluate the user's response (fluency, vocabulary, grammar)
2. Provide a score (0-9) based on IELTS criteria
3. Suggest 1-2 vocabulary upgrades if applicable
4. Generate a follow-up question to continue the discussion

## Response Format (JSON only)
{
  "aiMessage": "Your follow-up question or comment",
  "score": 6.5,
  "suggestions": ["brief feedback point 1", "brief feedback point 2"],
  "vocabUpgrades": [
    {
      "original": "what user said",
      "upgrade": "better expression",
      "reason": "why it's better"
    }
  ]
}

Return ONLY valid JSON.`;
}

/**
 * 生成对话开场白
 */
export async function generateOpeningQuestion(topic: string, targetBand: number): Promise<string> {
  const prompt = `You are an IELTS speaking examiner starting a Part 3 discussion.

Topic: ${topic}
Target band: ${targetBand}

Generate a thought-provoking opening question about this topic. The question should:
1. Be open-ended (not yes/no)
2. Encourage the candidate to give detailed opinions
3. Be appropriate for the target band level

Return ONLY the question, no other text.`;

  const response = await callGeminiText('', prompt);
  return response.trim();
}

/**
 * 处理用户的语音回复
 */
export async function processUserResponse(
  topic: string,
  targetBand: number,
  turns: DialogTurn[],
  audioBlob: Blob,
  mimeType: string
): Promise<DialogResponse & { transcript: string }> {
  // 先转录
  const transcriptPrompt = 'Transcribe this audio. Return ONLY the transcription, no other text.';
  const transcript = await callGeminiAudio({
    apiKey: '',
    blob: audioBlob,
    prompt: transcriptPrompt,
    mimeType,
  });

  // 然后分析并生成回复
  const dialogPrompt = buildDialogPrompt(topic, targetBand, turns, transcript.trim());
  const response = await callGeminiText('', dialogPrompt);

  // 解析响应
  try {
    const parsed = JSON.parse(response);
    return {
      transcript: transcript.trim(),
      aiMessage: String(parsed.aiMessage || ''),
      score: typeof parsed.score === 'number' ? parsed.score : undefined,
      suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions.map(String) : undefined,
      vocabUpgrades: Array.isArray(parsed.vocabUpgrades)
        ? parsed.vocabUpgrades.map((v: any) => ({
            original: String(v.original || ''),
            upgrade: String(v.upgrade || ''),
            reason: String(v.reason || ''),
          }))
        : undefined,
    };
  } catch {
    // 如果解析失败，尝试提取 JSON
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        transcript: transcript.trim(),
        aiMessage: String(parsed.aiMessage || response),
        score: typeof parsed.score === 'number' ? parsed.score : undefined,
        suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions.map(String) : undefined,
        vocabUpgrades: undefined,
      };
    }
    // 返回原始响应
    return {
      transcript: transcript.trim(),
      aiMessage: response,
    };
  }
}

/**
 * 生成对话总结
 */
export async function generateDialogSummary(
  topic: string,
  turns: DialogTurn[]
): Promise<{
  overallScore: number;
  strengths: string[];
  improvements: string[];
  vocabToLearn: string[];
}> {
  const historyLines = turns.map((turn) => {
    const role = turn.role === 'ai' ? 'AI' : 'User';
    return `${role}: ${turn.content}`;
  }).join('\n');

  const prompt = `Summarize this IELTS speaking practice session:

Topic: ${topic}

Conversation:
${historyLines}

Provide:
1. Overall band score estimate
2. 2-3 strengths
3. 2-3 areas for improvement
4. 3-5 vocabulary items to learn

Response format (JSON only):
{
  "overallScore": 6.5,
  "strengths": ["...", "..."],
  "improvements": ["...", "..."],
  "vocabToLearn": ["word1", "word2", "word3"]
}`;

  const response = await callGeminiText('', prompt);

  try {
    const parsed = JSON.parse(response);
    return {
      overallScore: typeof parsed.overallScore === 'number' ? parsed.overallScore : 6.0,
      strengths: Array.isArray(parsed.strengths) ? parsed.strengths.map(String) : [],
      improvements: Array.isArray(parsed.improvements) ? parsed.improvements.map(String) : [],
      vocabToLearn: Array.isArray(parsed.vocabToLearn) ? parsed.vocabToLearn.map(String) : [],
    };
  } catch {
    return {
      overallScore: 6.0,
      strengths: [],
      improvements: [],
      vocabToLearn: [],
    };
  }
}
